import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { TadManagerConfig } from "../../../../api/graphql";
import { TadManagerConfigListStore } from "../../store";

import { VisibilityIcon, EditIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: TadManagerConfigListStore };

export const TadManagerConfigListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<TadManagerConfig>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: TadManagerConfig, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: TadManagerConfig, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<TadManagerConfig>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "authProvider") {
          return {
            ...col,
            id: "authProvider",
            accessorFn: (row: TadManagerConfig) => {
              const v = (row as any).authProvider;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.TadAuthProviderOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "idCardType") {
          return {
            ...col,
            id: "idCardType",
            accessorFn: (row: TadManagerConfig) => {
              const v = (row as any).idCardType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.IdCardTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "gender") {
          return {
            ...col,
            id: "gender",
            accessorFn: (row: TadManagerConfig) => {
              const v = (row as any).gender;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.GenderOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns]);

    const rows = store.tadManagerConfigCurrentPage || [];

    return (
      <div id={"ListOfTadManagerConfig"}>
        <div id={"StartOfListingOfTadManagerConfig"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
